import type { BrandKit } from '@goldcast/api/backend';
import {
  BrandKitStore,
  PlatformBrandKitFile,
  PushFn,
  PlatformBrandKitFontWeightSlot,
  type PlatformFontEntry,
  PlatformBrandKitStore
} from '../types/brand-kit-types';
import {
  DEFAULT_FONT_MEDIUM,
  DEFAULT_FONT_REGULAR,
  FONT_DEFAULT_VALUE,
  FONT_STYLESHEET_EXACT_HOSTNAMES,
  FONT_STYLESHEET_HOSTNAME_SUFFIXES,
  FONTS_DEFAULT,
  PLATFORM_BRAND_KIT_PRESET_FONTS,
  WEIGHT_SLOT_KEY_RE
} from '../constants/fonts';
import { EnvironmentVariable, getEnvValue } from '../env';
import { getFontFullPath } from './fontPathUtils';
export { getFontFullPath };
import { FontItem } from '../types/font-types';
import { getFileNameFromPath, getFileNameWithoutExtension } from './fileUtils';
import { Clip } from '../types/clip-types';
import { BRAND_KIT_PATH } from '../constants/brandKit.constants';
import type { FontFormat } from '../types/RemotionPlayerConfigTypes/Font';
import { SelectionElementType } from '../types/RemotionPlayerConfigTypes/CompositionConfig';

export function getFontByLocation(location?: string): FontItem | undefined {
  if (!location) {
    return;
  }
  const fromList = FONTS_DEFAULT.find(f => f.url === location);
  if (fromList) {
    return fromList;
  }
  const preset = PLATFORM_BRAND_KIT_PRESET_FONTS.find(p => getFontFullPath(p.file) === location);
  if (preset) {
    return { name: preset.name, url: getFontFullPath(preset.file) };
  }
  return;
}

export const getCurrentClipFontNamePure = ({
  clip,
  fontLocation,
  clipBrandKitStore
}: {
  clip: Clip;
  clipBrandKitStore: BrandKitStore;
  fontLocation?: string;
}): string | null => {
  const assetMetadata = clip?.asset_metadata;
  const currentFont = fontLocation || assetMetadata?.caption_styles?.font_location || assetMetadata?.font_location;
  return currentFont && clipBrandKitStore
    ? clipBrandKitStore.fontsMap?.brandKit[currentFont] || clipBrandKitStore.fontsMap?.predefined?.[currentFont] || null
    : null;
};

export const getFontNameFromBrandKitPure = ({
  fontUrl,
  brandKitStore
}: {
  fontUrl?: string;
  brandKitStore: BrandKitStore;
}) => {
  if (!fontUrl) {
    return '';
  }
  return brandKitStore?.fontsMap?.[fontUrl] || '';
};

/** Basename without extension (or whole string if no dot); strips commas only (they break unquoted family names). */
export function displayNameFromPathOrFamily(raw: string): string {
  const base = getFileNameFromPath(raw.trim());
  const name = base.includes('.') ? getFileNameWithoutExtension(base) : base;
  return name.replace(/,/g, '');
}

/**
 * To be used only when fetching the Brand kit uploaded files
 * Do not use this for uploading files.
 * Should use encodeUploadBrandKitFileName for uploads.
 */
export function encodeBrandKitFileName(fileName: string): string {
  return encodeURI(fileName).replace('(', '%28').replace(')', '%29');
}

export const getBrandKitUrlPure = ({
  fileName,
  clipId,
  organization
}: {
  fileName: string;
  clipId?: string;
  organization?: string | null;
}) => {
  const baseFilePath = clipId ? `${organization}/clips/${clipId}` : `${organization}`;
  const encodedFileName = encodeBrandKitFileName(fileName);
  return `${getEnvValue(EnvironmentVariable.CLOUDFRONT_STATIC_ASSET_URL)}${BRAND_KIT_PATH}/${baseFilePath}/${encodedFileName}`;
}; /** Derives Remotion-compatible format from a file extension in a URL or filename. */

export function deriveFontFormat(source: string): FontFormat | undefined {
  const ext = source.split('.').pop()?.toLowerCase().split('?')[0];
  const map: Record<string, FontFormat> = { ttf: 'truetype', otf: 'opentype', woff: 'woff', woff2: 'woff2' };
  return map[ext ?? ''];
}

/** Parses a `cl_fonts` / legacy fonts list item (string path, `{ url, originalName }`, or preset-only web_safe). */
export function parseFontListItem(item: unknown, push: PushFn, organization: string | undefined | null): void {
  if (typeof item === 'string') {
    const url = item.startsWith('http') ? item : getBrandKitUrlPure({ fileName: item, organization });
    push(url, displayNameFromPathOrFamily(item), deriveFontFormat(item));
    return;
  }
  if (!item || typeof item !== 'object') {
    return;
  }
  const row = item as Partial<PlatformBrandKitFile>;
  const urlTrimmed = row.url && typeof row.url === 'string' ? row.url.trim() : '';
  if (urlTrimmed) {
    const url = resolveFontFileUrlPure({ maybePathOrUrl: urlTrimmed, organization });
    const fileLabel = row.originalName
      ? displayNameFromPathOrFamily(row.originalName)
      : displayNameFromPathOrFamily(urlTrimmed);
    const format = deriveFontFormat(urlTrimmed) ?? deriveFontFormat(row.originalName ?? '');
    push(url, fileLabel, format);
    return;
  }
  const originalName = row.originalName;
  if (originalName && typeof originalName === 'string') {
    // No URL was stored — this is a web-safe system font (Arial, Georgia, Helvetica, etc.).
    // Use the originalName directly as the CSS font-family identifier. No file loading is
    // needed: the browser and rendering environment use the natively installed typeface.
    const familyName = originalName.trim();
    const label = displayNameFromPathOrFamily(originalName);
    if (familyName && label) {
      push(familyName, label);
    }
  }
}

export const resolveFontFileUrlPure = ({
  maybePathOrUrl,
  organization
}: {
  maybePathOrUrl: string;
  organization: string | null | undefined;
}) => {
  const t = maybePathOrUrl.trim();
  if (t.startsWith('http://') || t.startsWith('https://')) {
    return t;
  }
  return getBrandKitUrlPure({ fileName: t, organization });
};
export function fontMapKeySortKey(key: string): [number, number, string] {
  if (key === 'web_font') {
    return [0, 0, key];
  }
  if (key === 'web_safe_font') {
    return [2, 0, key];
  }
  const m = /^weight_(\d+)$/.exec(key);
  if (m) {
    return [1, Number(m[1]), key];
  }
  return [3, 0, key];
}
export function sortFontMapKeys(keys: string[]): string[] {
  return [...keys].sort((a, b) => {
    const [a0, a1, aKey] = fontMapKeySortKey(a);
    const [b0, b1, bKey] = fontMapKeySortKey(b);
    if (a0 !== b0) {
      return a0 - b0;
    }
    if (a1 !== b1) {
      return a1 - b1;
    }
    return aKey.localeCompare(bKey);
  });
} /** Label for a weight slot, preferring `originalName` over URL basename. */
export function displayNameForWeightSlot(row: { originalName?: string; url?: string }): string {
  const fromOriginal =
    row.originalName && typeof row.originalName === 'string' ? displayNameFromPathOrFamily(row.originalName) : '';
  const fromUrl = row.url && typeof row.url === 'string' ? displayNameFromPathOrFamily(row.url) : '';
  return fromOriginal || fromUrl || '';
} /**
 * True for `http(s)` stylesheet imports (Google/Adobe Fonts hosts, `.css` URLs); extension-less CDN font URLs stay false so FontFace can load them.
 */

export function isFontStylesheetUrl(url: string): boolean {
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return false;
  }
  if (/\.css(?:[?#]|$)/i.test(url)) {
    return true;
  }
  try {
    const { hostname } = new URL(url);
    return (
      FONT_STYLESHEET_EXACT_HOSTNAMES.has(hostname) ||
      FONT_STYLESHEET_HOSTNAME_SUFFIXES.some(suffix => hostname.endsWith(suffix))
    );
  } catch {
    return false;
  }
}

/** Pushes the `web_font` slot (CSS or file URL plus optional family name). */
export function pushFromWebFontSlot(value: object, push: PushFn, organization: string | null | undefined): void {
  if (!value || typeof value !== 'object') {
    return;
  }
  const row = value as Partial<PlatformBrandKitFile>;
  const urlRaw = row.url?.trim() ?? '';
  if (!urlRaw) {
    return;
  }
  const url = resolveFontFileUrlPure({ maybePathOrUrl: urlRaw, organization });
  const name = row.originalName?.trim() || displayNameFromPathOrFamily(urlRaw);
  // CSS stylesheet URLs (Google Fonts etc.) have no binary format; file URLs may have one
  const format = isFontStylesheetUrl(url) ? undefined : deriveFontFormat(urlRaw);
  push(url, name, format);
}

/** Pushes a `weight_*` slot (resolved file URL and display label). */
export function pushFromWeightSlot(
  key: string,
  value: object,
  push: PushFn,
  organization: string | undefined | null
): void {
  if (!value || typeof value !== 'object') {
    return;
  }
  const row = value as Partial<PlatformBrandKitFontWeightSlot>;
  const urlRaw = row.url && typeof row.url === 'string' ? row.url.trim() : '';
  if (!urlRaw) {
    return;
  }
  const url = resolveFontFileUrlPure({ maybePathOrUrl: urlRaw, organization });
  const name = displayNameForWeightSlot(row);
  // Prefer extension from originalName (e.g. "Roboto-VariableFont_wdth,wght.ttf") over extension-less Filestack URL
  const format = deriveFontFormat(row.originalName ?? '') ?? deriveFontFormat(urlRaw);
  push(url, name, format);
}

/** Iterates `fonts` (array or keyed map) in slot order and pushes entries via `push`. */
export function pushEntriesFromFontsField(
  rawFonts: unknown,
  push: PushFn,
  organization: string | undefined | null
): void {
  if (rawFonts === undefined || rawFonts === null) {
    return;
  }
  if (Array.isArray(rawFonts)) {
    for (const item of rawFonts) {
      parseFontListItem(item, push, organization);
    }
    return;
  }
  if (typeof rawFonts !== 'object') {
    return;
  }
  const record = rawFonts as Record<string, unknown>;
  for (const key of sortFontMapKeys(Object.keys(record))) {
    const value = record[key];
    if (value === null || value === undefined) {
      continue;
    }
    if (typeof value !== 'object' || Array.isArray(value)) {
      continue;
    }
    if (key === 'web_font') {
      pushFromWebFontSlot(value, push, organization);
      continue;
    }
    if (WEIGHT_SLOT_KEY_RE.test(key)) {
      pushFromWeightSlot(key, value, push, organization);
      continue;
    }
    if (key === 'web_safe_font') {
      parseFontListItem(value, push, organization);
      continue;
    }
    const row = value as Partial<PlatformBrandKitFile>;
    const hasUrl = typeof row.url === 'string' && row.url.trim().length > 0;
    const hasName = typeof row.originalName === 'string' && row.originalName.trim().length > 0;
    if (hasUrl || hasName) {
      parseFontListItem(value, push, organization);
    }
  }
}
export const getFontEntriesFromPlatformBrandKitPure = (
  kit: BrandKit | undefined,
  organization: string | null | undefined
): PlatformFontEntry[] => {
  if (!kit) {
    return [];
  }
  const out: PlatformFontEntry[] = [];
  const seen = new Set<string>();

  const push: PushFn = (url, name, format) => {
    if (!url || seen.has(url)) {
      return;
    }
    seen.add(url);
    out.push({ url, name: name || displayNameFromPathOrFamily(url), format });
  };

  pushEntriesFromFontsField(kit.fonts as unknown, push, organization);

  const rawCl = (kit as BrandKit & { cl_fonts?: unknown }).cl_fonts;
  if (Array.isArray(rawCl) && rawCl.length > 0) {
    for (const item of rawCl) {
      parseFontListItem(item, push, organization);
    }
  }

  return out;
};
export const getPlatformFontEntryByUrlPure = ({
  fontLocation,
  platformBrandKit,
  organization
}: {
  fontLocation: string | undefined;
  platformBrandKit: PlatformBrandKitStore;
  organization: string | null | undefined;
}) => {
  if (!fontLocation) {
    return undefined;
  }
  const { availableBrandKits, ftuxBrandKit } = platformBrandKit;
  for (const kit of availableBrandKits) {
    const entry = getFontEntriesFromPlatformBrandKitPure(kit, organization).find(e => e.url === fontLocation);
    if (entry) {
      return entry;
    }
  }
  const ftuxFonts = (ftuxBrandKit?.fonts as string[] | undefined) ?? [];
  const ftuxFontPath = ftuxFonts.find(
    fontPath => resolveFontFileUrlPure({ maybePathOrUrl: fontPath, organization }) === fontLocation
  );
  if (ftuxFontPath) {
    return {
      url: resolveFontFileUrlPure({ maybePathOrUrl: ftuxFontPath, organization }),
      name: displayNameFromPathOrFamily(ftuxFontPath),
      format: deriveFontFormat(ftuxFontPath)
    };
  }
  return undefined;
};
export const getFontFamilyStylePure = ({
  clip,
  fontLocation,
  clipBrandKitStore,
  brandKitStore,
  platformBrandKit,
  organization
}: {
  fontLocation?: string;
  clip: Clip;
  clipBrandKitStore: BrandKitStore;
  brandKitStore: BrandKitStore;
  platformBrandKit: PlatformBrandKitStore;
  organization?: string | null | undefined;
}) => {
  const fontItem = getFontByLocation(fontLocation);
  if (fontItem) {
    return fontItem.name;
  }
  // No standard font, get from brand kit
  const currentClipFontName = getCurrentClipFontNamePure({
    clip,
    clipBrandKitStore,
    fontLocation
  });
  if (currentClipFontName) {
    return currentClipFontName;
  }

  const brandKitFontName = getFontNameFromBrandKitPure({
    fontUrl: fontLocation,
    brandKitStore
  });
  if (brandKitFontName) {
    return brandKitFontName;
  }

  // Platform brand kit fonts (filestack URLs, web_safe_font presets, web_font CSS URLs, etc.)
  // are not in any of the legacy stores above — look them up from the platform brand kit store.
  const platformEntry = getPlatformFontEntryByUrlPure({
    fontLocation,
    platformBrandKit,
    organization
  });
  if (platformEntry) {
    return platformEntry.name;
  }

  return undefined;
};
export const getFontLocationFromAssetMetadata = (
  assetMetadata: Clip['asset_metadata'],
  fontTarget: SelectionElementType
) => {
  if (fontTarget === SelectionElementType.SPEAKER_LABEL) {
    return assetMetadata.speaker_label_styles?.font_location || FONT_DEFAULT_VALUE.url;
  }
  if (fontTarget === SelectionElementType.SPEAKER_LABEL_TITLE) {
    return (
      assetMetadata.speaker_label_styles?.title_font_location ||
      assetMetadata.speaker_label_styles?.font_location ||
      FONT_DEFAULT_VALUE.url
    );
  }
  return assetMetadata?.caption_styles?.font_location || assetMetadata?.font_location || FONT_DEFAULT_VALUE.url;
};
export function getDefaultFontProperties(isRegular: boolean = true): {
  font: string;
  font_location: string;
} {
  const font = isRegular ? DEFAULT_FONT_REGULAR : DEFAULT_FONT_MEDIUM;
  return {
    font,
    font_location: `${getEnvValue(EnvironmentVariable.CLOUDFRONT_STATIC_ASSET_URL)}content-lab/fonts/${font}`
  };
}
export const getFontObject = ({
  clip,
  fontTarget,
  isPlatformBrandKitEnabled,
  clipBrandKitStore,
  brandKitStore,
  platformBrandKit,
  organization
}: {
  clip: Clip;
  fontTarget: SelectionElementType;
  isPlatformBrandKitEnabled: boolean;
  clipBrandKitStore: BrandKitStore;
  brandKitStore: BrandKitStore;
  platformBrandKit: PlatformBrandKitStore;
  organization?: string | null | undefined;
}) => {
  const fontLocation = getFontLocationFromAssetMetadata(clip.asset_metadata, fontTarget);
  const fontName = getFontFamilyStylePure({
    clip,
    fontLocation,
    clipBrandKitStore,
    brandKitStore,
    platformBrandKit,
    organization
  });

  if (fontName && fontLocation) {
    const platformEntry = isPlatformBrandKitEnabled
      ? getPlatformFontEntryByUrlPure({ fontLocation, platformBrandKit, organization })
      : undefined;
    return { font: { location: fontLocation, name: fontName, format: platformEntry?.format } };
  }
  const { font_location: location, font: name } = getDefaultFontProperties(true);
  return { font: { location, name } };
};
